import React, { useCallback, useEffect, useState } from 'react';
import { apiService } from '../../services/api';

interface SuggestionRow {
  id: number;
  suggestion_type: 'asset' | 'indication' | string;
  value: string;
  status: 'pending' | 'approved' | 'rejected' | string;
  suggestion_count: number;
  suggested_by_user_id: number;
  suggested_by_email?: string | null;
  last_suggested_at: string | null;
  reviewed_by_email?: string | null;
  reviewed_at?: string | null;
  approved_entity_id?: number | null;
  notes?: string | null;
}

/** Superuser queue: user-followed/searched assets & indications awaiting catalog approval. */
const ManageTrialTrackerSuggestions: React.FC = () => {
  const [rows, setRows] = useState<SuggestionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'pending' | 'approved' | 'rejected' | 'all'>('pending');
  const [typeFilter, setTypeFilter] = useState<'all' | 'asset' | 'indication'>('all');
  const [busyId, setBusyId] = useState<number | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.fetchTrialTrackerSuggestions({
        status: statusFilter,
        type: typeFilter === 'all' ? undefined : typeFilter,
      });
      if (res?.success) setRows(res.suggestions || []);
      else setError(res?.error || 'Failed to load suggestions.');
    } catch {
      setError('Failed to load suggestions.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, typeFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const approve = async (id: number) => {
    setBusyId(id);
    setMessage(null);
    try {
      const res = await apiService.approveTrialTrackerSuggestion(id);
      if (res?.success) {
        setMessage(`Approved — added to shared catalog (id ${res.suggestion?.approved_entity_id}).`);
        await load();
      } else setError(res?.error || 'Approve failed.');
    } catch {
      setError('Approve failed.');
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (id: number) => {
    setBusyId(id);
    setMessage(null);
    try {
      const res = await apiService.rejectTrialTrackerSuggestion(id);
      if (res?.success) {
        setMessage('Rejected — will not enter shared scrape.');
        await load();
      } else setError(res?.error || 'Reject failed.');
    } catch {
      setError('Reject failed.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Trial Tracker suggestions</h1>
        <p className="text-sm text-slate-500 mt-1 max-w-2xl">
          Users can follow any asset or indication. Only approved names are added to the shared
          catalog (<code className="text-xs">emerging_assets_list</code> /{' '}
          <code className="text-xs">emerging_disease_indications</code>) and the overall scrape.
        </p>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        {(['pending', 'approved', 'rejected', 'all'] as const).map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 rounded-lg text-sm font-semibold border ${
              statusFilter === s
                ? 'bg-indigo-600 text-white border-indigo-600'
                : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-300'
            }`}
          >
            {s}
          </button>
        ))}
        <span className="text-slate-300 mx-1">|</span>
        {(['all', 'asset', 'indication'] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTypeFilter(t)}
            className={`px-3 py-1.5 rounded-lg text-sm font-semibold border ${
              typeFilter === t
                ? 'bg-slate-800 text-white border-slate-800'
                : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {message && <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2">{message}</div>}
      {error && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">{error}</div>}

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
        {loading ? (
          <div className="p-8 text-sm text-slate-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-sm text-slate-500">No suggestions in this filter.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Value</th>
                <th className="px-4 py-3">Suggested by</th>
                <th className="px-4 py-3">Count</th>
                <th className="px-4 py-3">Last</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => (
                <tr key={r.id} className="hover:bg-slate-50/80">
                  <td className="px-4 py-3 font-medium text-slate-700">{r.suggestion_type}</td>
                  <td className="px-4 py-3 font-semibold text-slate-900">{r.value}</td>
                  <td className="px-4 py-3 text-slate-600">
                    {r.suggested_by_email || `user #${r.suggested_by_user_id}`}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{r.suggestion_count}</td>
                  <td className="px-4 py-3 text-slate-500 whitespace-nowrap">
                    {r.last_suggested_at ? new Date(r.last_suggested_at).toLocaleString() : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${
                        r.status === 'pending'
                          ? 'bg-amber-50 text-amber-800'
                          : r.status === 'approved'
                            ? 'bg-emerald-50 text-emerald-800'
                            : 'bg-slate-100 text-slate-600'
                      }`}
                    >
                      {r.status}
                      {r.approved_entity_id ? ` #${r.approved_entity_id}` : ''}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right space-x-2 whitespace-nowrap">
                    {r.status !== 'approved' && (
                      <button
                        type="button"
                        disabled={busyId === r.id}
                        onClick={() => approve(r.id)}
                        className="px-2.5 py-1 rounded-lg text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40"
                      >
                        Approve
                      </button>
                    )}
                    {r.status !== 'rejected' && (
                      <button
                        type="button"
                        disabled={busyId === r.id}
                        onClick={() => reject(r.id)}
                        className="px-2.5 py-1 rounded-lg text-xs font-semibold bg-white border border-slate-200 text-slate-700 hover:border-rose-300 hover:text-rose-700 disabled:opacity-40"
                      >
                        Reject
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default ManageTrialTrackerSuggestions;
